import { Alert, Button, Empty, Spin, Tag } from "antd";
import type { ReactNode } from "react";

import type { RunStatus } from "../api/types";
import { RUN_STATUS_TITLES } from "../api/types";

export type Tone = "ok" | "warn" | "error" | "info" | "source" | "muted";

const TONE_COLOR: Readonly<Record<Tone, string>> = {
  ok: "var(--kit-success)",
  warn: "var(--kit-warning)",
  error: "var(--kit-error)",
  info: "var(--kit-primary)",
  source: "var(--kit-primary-7)",
  muted: "var(--kit-text-tertiary)",
};

const STATUS_TONE: Readonly<Record<RunStatus, Tone>> = {
  running: "info",
  success: "ok",
  failed: "error",
  cancelled: "muted",
};

function toneOfStatus(status: string | null | undefined): Tone {
  return status && status in STATUS_TONE
    ? STATUS_TONE[status as RunStatus]
    : "muted";
}

export function StatusTag({
  status = null,
  tone = null,
  label = "",
  fill = "outline",
}: {
  status?: RunStatus | (string & {}) | null;
  tone?: Tone | null;
  label?: string;
  fill?: "solid" | "outline";
}) {
  const known = status && status in STATUS_TONE ? (status as RunStatus) : null;
  const resolved = tone ?? toneOfStatus(status);
  const text = label
    ? label
    : known
      ? RUN_STATUS_TITLES[known]
      : (status ?? "—");
  const color = TONE_COLOR[resolved];

  return (
    <Tag
      variant={fill === "solid" ? "solid" : "outlined"}
      style={{
        color: fill === "solid" ? "var(--kit-bg-secondary)" : color,
        background: fill === "solid" ? color : "transparent",
        borderColor: color,
        marginInlineEnd: 0,
      }}
    >
      {status === "running" ? <span style={{ marginRight: 4 }}>●</span> : null}
      {text}
    </Tag>
  );
}

export function Loading({
  label = "",
  block = false,
}: {
  label?: string;
  block?: boolean;
}) {
  if (block) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 10,
          padding: "40px 16px",
          color: "var(--kit-text-tertiary)",
        }}
      >
        <Spin />
        {label ? <span className="muted">{label}</span> : null}
      </div>
    );
  }

  return (
    <span
      className="faint"
      style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
    >
      <Spin size="small" />
      {label ? (
        <span style={{ fontSize: "var(--kit-font-size-sm)" }}>{label}</span>
      ) : null}
    </span>
  );
}

export function Blank({
  title = "Пусто",
  hint = "",
  children,
}: {
  title?: string;
  hint?: string;
  children?: ReactNode;
}) {
  return (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        <div className="stack" style={{ gap: 2 }}>
          <span>{title}</span>
          {hint ? <span className="muted">{hint}</span> : null}
        </div>
      }
      style={{ padding: "24px 16px" }}
    >
      {children}
    </Empty>
  );
}

export function Failure({
  text,
  hint = null,
  onRetry,
}: {
  text: string;
  hint?: string | null;
  onRetry?: () => void;
}) {
  return (
    <Alert
      type="error"
      showIcon
      title={text}
      description={hint}
      action={
        onRetry ? (
          <Button size="small" type="link" onClick={onRetry}>
            Повторить
          </Button>
        ) : null
      }
    />
  );
}
